/**
 * Drone D1 #322 — the live drone feed embedded on the COP. Given a mission, it asks the server
 * for the feed descriptor (the live-only gate + embed classification live on the server, behind
 * the SSRF guard) and renders the matching camera-path player:
 *   - hls      → LiveCameraPlayer pointed at the same-origin SSRF-guarded proxy path
 *   - youtube  → YouTubeLivePlayer pointed at the same-origin resolver path
 *   - anything else / not live → an HONEST "feed unavailable" card (never a fake/blank player)
 * The client NEVER touches the operator's feed_url — only the server's same-origin paths.
 */
import { useEffect, useState } from 'react';
import { fetchMissionFeed, type DroneMissionView } from './api';
import type { MissionFeedDescriptor } from '@tsr/shared-types';
import { LiveCameraPlayer } from './LiveCameraPlayer';
import { YouTubeLivePlayer } from './YouTubeLivePlayer';

function FeedUnavailable({ reason }: { reason: string }): JSX.Element {
  return (
    <div
      className="rounded border border-slate-700 bg-slate-900/60 px-3 py-4 text-center"
      data-testid="mission-feed-unavailable"
    >
      <svg
        className="mx-auto h-6 w-6 text-slate-500"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72v12.44l-4.72-4.72M4.5 6.75h7.5a1.5 1.5 0 011.5 1.5v7.5a1.5 1.5 0 01-1.5 1.5h-7.5a1.5 1.5 0 01-1.5-1.5v-7.5a1.5 1.5 0 011.5-1.5z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
      </svg>
      <p className="mt-1.5 text-xs font-medium text-slate-300">Live feed not available</p>
      <p className="mt-0.5 text-[11px] text-slate-500 leading-relaxed">{reason}</p>
    </div>
  );
}

export function MissionFeedEmbed({ mission }: { mission: DroneMissionView }): JSX.Element {
  const [feed, setFeed] = useState<MissionFeedDescriptor | null | undefined>(undefined);

  useEffect(() => {
    const ctrl = new AbortController();
    setFeed(undefined);
    void fetchMissionFeed(mission.id, ctrl.signal)
      .then((d) => setFeed(d))
      .catch(() => setFeed(null));
    return () => ctrl.abort();
    // Re-resolve when the mission moves between states (e.g. confirmed → enroute makes it live).
  }, [mission.id, mission.status]);

  const label = `Drone feed · ${mission.operator_name ?? 'Operator'}`;

  if (feed === undefined) {
    return (
      <div
        className="rounded border border-slate-700 bg-slate-900/60 px-3 py-4 text-center text-[11px] text-slate-500"
        data-testid="mission-feed-loading"
      >
        Resolving live feed…
      </div>
    );
  }

  if (!feed || !feed.live || feed.embed === 'unplayable') {
    return (
      <FeedUnavailable
        reason={feed?.reason ?? 'No live feed is available for this mission right now.'}
      />
    );
  }

  if (feed.embed === 'hls' && feed.stream_path) {
    return (
      <div className="overflow-hidden rounded border border-slate-700" data-testid="mission-feed-hls">
        <LiveCameraPlayer
          cameraId={mission.id}
          streamUrl={feed.stream_path}
          useProxy={false}
          name={label}
          streamType="hls"
          enablePopout={false}
        />
      </div>
    );
  }

  if (feed.embed === 'youtube' && feed.youtube_path) {
    return (
      <div className="overflow-hidden rounded border border-slate-700" data-testid="mission-feed-youtube">
        <YouTubeLivePlayer
          cameraId={mission.id}
          name={label}
          resolveUrl={feed.youtube_path}
          enablePopout={false}
        />
      </div>
    );
  }

  // Server said embeddable but gave no path (shouldn't happen) — honest fallback.
  return <FeedUnavailable reason="The live feed could not be resolved." />;
}
